import { constantsJson, operatorSymbolsJson } from "@corca/data";
import { BigNumber } from "bignumber.js";

import { SYMPY_UNIT_TO_DISPLAY } from "../unitToSympyMap";
import {
  constantNode,
  groupNode,
  inlineNode,
  matrixNode,
  numberNode,
  operatorNode,
  operatorSymbolNode,
  variableNode,
} from "../utils";

import { buildUnitNode, prependMinus, prependPlus } from "./shared";

import type { SympyResult } from "../types";
import type { SympyParser } from "./SympyParser";
import type { NodeJson } from "@corca/data";

const minusSymbolJson = operatorSymbolsJson.find((op) => op.key === "symbol-subtraction")!;
const imaginaryJson = constantsJson.find(
  (c) => c.symbolProps?.key === "constant-imaginary-number-i",
)!;
const infinityJson = constantsJson.find((c) => c.symbolProps?.key === "constant-infinity")!;
const commaSymbolJson = operatorSymbolsJson.find((op) => op.key === "symbol-comma")!;
const plusMinusSymbolJson = operatorSymbolsJson.find((op) => op.key === "symbol-plus-minus")!;

/**
 * Parse a SympyResult into a NodeJson array, transforming power structures into more readable forms.
 * Can throw errors if the SympyResult is malformed or contains unknown types.
 */
export function parseResult(
  parser: SympyParser,
  sympy: SympyResult,
  opts?: { signed?: boolean; parent?: SympyResult; grouped?: boolean; exponent?: boolean },
): NodeJson[] {
  const { signed } = opts ?? {};
  let result: NodeJson[] = [];

  if (Array.isArray(sympy)) {
    if (sympy.length === 0) {
      return [];
    }

    // No need to wrap in a group if there's only one element
    if (sympy.length === 1) {
      return parser.parseResult(sympy[0]);
    }

    // Nested arrays (tuples from system solve) render with parentheses
    const isNested = opts?.grouped;
    const openBracket = isNested ? "(" : "{";
    const closeBracket = isNested ? ")" : "}";

    // Check if this is a list of tuples (system solve result)
    const hasTuples = sympy.some((item) => Array.isArray(item));

    const nodes: NodeJson[] = [];
    for (let i = 0; i < sympy.length; i++) {
      const arg = parser.parseResult(sympy[i], {
        signed: i === 0 ? signed : false,
        grouped: hasTuples,
      });
      if (i > 0) nodes.push(operatorSymbolNode(commaSymbolJson));
      nodes.push(...arg);
    }

    return [groupNode(openBracket, closeBracket, [matrixNode(1, 1, [inlineNode(...nodes)])])];
  }

  switch (sympy.type) {
    case "symbol": {
      const originalNode = parser.symbolNodes?.get(sympy.name);
      result = [
        originalNode
          ? ({ ...originalNode, children: [...originalNode.children] } as NodeJson)
          : variableNode(sympy.name),
      ];
      break;
    }

    case "infinity":
      switch (sympy.kind) {
        case "positive":
          result = [constantNode(infinityJson)];
          break;
        case "negative":
          result = prependMinus(constantNode(infinityJson));
          break;
        case "complex":
          result = [constantNode(infinityJson)];
          break;
      }
      break;

    case "number":
      // Sign was lifted into an enclosing Neg by normalizeSemantic.
      result = [numberNode(formatNumber(sympy.kind, sympy.value))];
      break;

    case "imaginary_unit":
      result = [constantNode(imaginaryJson)];
      break;

    case "rational":
      // Unreduced rationals are rewritten to Frac (and any sign to Neg) by normalizeSemantic;
      // this renders the leaf fraction as a defensive fallback.
      result = [
        operatorNode("fraction", [
          inlineNode(numberNode(formatNumber("integer", sympy.p))),
          inlineNode(numberNode(formatNumber("integer", sympy.q))),
        ]),
      ];
      break;

    case "function":
      result = parser.parseSympyFunction(sympy, { signed });
      break;

    case "operator":
      result = parser.parseSympyOperator(sympy, opts);
      break;

    case "matrix":
      result = [
        groupNode("[", "]", [
          matrixNode(
            sympy.rows,
            sympy.cols,
            sympy.data.map((arg) => inlineNode(...parser.parseResult(arg))),
          ),
        ]),
      ];
      break;

    case "unit": {
      const displayText = SYMPY_UNIT_TO_DISPLAY[sympy.name];
      if (!displayText) {
        result = [variableNode(sympy.name)];
      } else {
        // Bare Quantity result (sympy drops a 1 coefficient): render as "1 kg"
        result = [
          {
            ...numberNode("1"),
            children: [buildUnitNode([{ display: displayText, exponent: 1 }])],
          },
        ];
      }
      break;
    }

    case "plus_minus": {
      const realNodes = parser.parseResult(sympy.real, { signed });
      const imagNodes = parser.parseResult(sympy.imag);
      result = [
        ...realNodes,
        operatorSymbolNode(plusMinusSymbolJson),
        ...imagNodes,
        constantNode(imaginaryJson),
      ];
      break;
    }
  }

  if (signed && result.length > 0) {
    if (result[0].type === "operatorSymbol" && result[0].props.name === minusSymbolJson.name) {
      return result; // Already signed
    }

    return prependPlus(...result);
  }

  return result;
}

function isScientificNumberNotation(str: string) {
  return str.includes("e") || str.includes("E");
}

function toScientificNotation(num: BigNumber): string {
  return num.toExponential(10).replace(/\.?[0]+e/, "e");
}

function formatNumber(kind: "integer" | "float", num: string) {
  const MAX_DIGITS = 20; // Threshold for switching to scientific notation
  const n = BigNumber(num);

  switch (kind) {
    case "integer":
      if (num.length > MAX_DIGITS) {
        return toScientificNotation(n);
      }
      return num;

    case "float":
      if (num.indexOf(".") > MAX_DIGITS) {
        return toScientificNotation(n);
      }
      if (isScientificNumberNotation(num) && !n.isInteger()) {
        return toScientificNotation(n);
      }
      return n.toString();
  }
}
